/*
 * -----------------------------------------------------------------------
 *  <copyright file="new-user-invitation.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { Visibility, VisibilityOff } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import getOrganisation from 'apis/get-organisation';
import { useBoolean, useTranslation, useLogger } from 'hooks';
import Settings from 'settings/settings';
import Constants from 'utils/constants';
import { validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons/button';
import FormControl from 'views/components/core/form-control';
import IconButton from 'views/components/core/icon-button';
import InputAdornment from 'views/components/core/input-adornment';
import Loading from 'views/components/core/loading';
import PasswordRequirementsWarning from 'views/components/core/password-requirements-warning';
import TextField from 'views/components/core/text-field';

import '../../layouts/login/login.scss';
import './uam-styles.scss';
import { AllRoutes } from '@utils/router/all-routes';

const NewUserInvitation = ({
    organisationKey,
    organisationCode,
    member,
}: {
    organisationKey: string;
    organisationCode: string;
    member: any;
}) => {
    const [state, setState] = useState({
        email: member.inviteEmail,
        name: '',
        password: '',
        showPassword: false,
        confirmPassword: '',
        showConfirmPassword: false,
    });
    const [errors, setErrors] = useState({} as any);
    const loading = useBoolean(false);
    const Logger = useLogger();
    const passwordIsInvalid = useBoolean(false);
    const disableBtn = useBoolean(true);
    const [organisation, setOrganisation] = useState(null);
    const { t } = useTranslation();
    // const { setToken } = useContext(UserContext);
    const navigate = useNavigate();

    // Load the organisation details (once)
    useEffect(() => {
        loading.setTrue();
        getOrganisation(organisationCode)
            .then((org: any) => {
                if (org) {
                    if (org.error) Logger.error('Error loading organisation: ', org.error);
                    else setOrganisation(org);
                }
            })
            .catch((error: any) => {
                Logger.error('Error loading organisation:', error);
            })
            .finally(() => {
                loading.setFalse();
            });
    }, [organisationCode]);

    const validateOnAccept = () => {
        const localErrors: any = {};

        const errorNewPassword = validateFields(state.password, 'password', 60, true);
        const errorConfirmedPassword = validateFields(state.confirmPassword, 'confirmPassword', 60, true);

        localErrors.password = errorNewPassword.password;
        localErrors.confirmPassword = errorConfirmedPassword.confirmPassword;

        if (!localErrors.password && !localErrors.confirmPassword) {
            if (passwordIsInvalid.value) {
                localErrors.password = t('uam:incorrectPasswordFormat');
                if (state.confirmPassword.length) localErrors.confirmPassword = t('uam:incorrectPasswordFormat');
            } else if (state.password !== state.confirmPassword && state.confirmPassword.length) {
                localErrors.password = t('uam:passwordsDontMatch');
                localErrors.confirmPassword = t('uam:passwordsDontMatch');
            }
        }

        setErrors({
            ...errors,
            ...localErrors,
        });

        return Object.keys(localErrors).filter((item) => localErrors[item]).length > 0;
    };

    const acceptInvite = () => {
        if (validateOnAccept()) return;

        loading.setTrue();

        fetch(`${Settings.api.url()}/api/accept-organisation-invite`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                organisationKey,
                memberId: member.id,
                // NOTE: Password has to match the security requirements here or this will fail (invisibly)
                password: state.password.trim(),
            }),
        })
            .then((response) => {
                loading.setFalse();
                return response.json();
            })
            .then((token) => {
                // Set token
                if (token && token.token) {
                    navigate(`${AllRoutes.path('login-organisation', { orgCode: organisationCode })}?complete=true`);
                } else {
                    setErrors({
                        ...errors,
                        password: t('uam:incorrectPasswordFormat'),
                    });
                }
            })
            .catch((error: any) => {
                Logger.error('Error accepting invitation: ', error);
                setErrors({
                    ...errors,
                    password: t('uam:incorrectPasswordFormat'),
                });
            });
    };

    if (loading.value) return <Loading />;

    if (!organisation) return <div>Unable to load organisation details.</div>;

    if (loading.value) return <Loading />;

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const validateError = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            password: null,
            confirmPassword: null,
            ...validateError,
        });
    };

    const onChange = (name: string) => (event: any) => {
        disableBtn.setFalse();
        const { value } = event.target;

        setState({
            ...state,
            [name]: value.trim(),
        });

        validate(event.target.value.trim(), name, 60, false);
    };

    return (
        <div className="login-page">
            <h1 className="login-title">{t('uam:completeRegistration')}</h1>

            <div>
                <PasswordRequirementsWarning
                    security={organisationCode ? organisation?.security : Constants.TRISecuritySettings}
                    password={state.password.trim()}
                    onPasswordValidate={(value: boolean) => passwordIsInvalid.setValue(value)}
                />
            </div>

            <form className="login-page-form mt" onSubmit={acceptInvite}>
                <FormControl className="form-control error" fullWidth required style={{ marginBottom: 30 }}>
                    <TextField
                        error={!!errors.password}
                        label={`${t('uam:newPassword')}*`}
                        id="password"
                        type={state.showPassword ? 'text' : 'password'}
                        rows={1}
                        fullWidth
                        value={state.password}
                        onChange={onChange('password')}
                        placeholder={t('uam:enterYourPassword')}
                        variant="outlined"
                        color="secondary"
                        helperText={errors.password || null}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        id="toggle-password-visibility"
                                        aria-label={t('togglePasswordVisibility')}
                                        onClick={() => setState({ ...state, showPassword: !state.showPassword })}
                                        onMouseDown={(e: any) => e.preventDefault()}
                                    >
                                        {state.showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </FormControl>
                <FormControl className="form-control error" fullWidth required style={{ marginBottom: 30 }}>
                    <TextField
                        error={!!errors.confirmPassword}
                        label={`${t('uam:reEnterNewPassword')}*`}
                        id="confirm-password"
                        type={state.showConfirmPassword ? 'text' : 'password'}
                        rows={1}
                        fullWidth
                        value={state.confirmPassword}
                        onChange={onChange('confirmPassword')}
                        placeholder={t('uam:reEnterNewPassword')}
                        variant="outlined"
                        color="secondary"
                        helperText={errors.confirmPassword || null}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        id="toggle-confirm-password-visibility"
                                        aria-label={t('togglePasswordVisibility')}
                                        onClick={() =>
                                            setState({ ...state, showConfirmPassword: !state.showConfirmPassword })
                                        }
                                        onMouseDown={(e: any) => e.preventDefault()}
                                    >
                                        {state.showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </FormControl>
                <Button
                    onClick={acceptInvite}
                    secondary
                    disabled={
                        Object.keys(errors).filter((item: any) => errors[item]).length > 0 ||
                        loading.value ||
                        disableBtn.value
                    }
                    text="confirm"
                    style={{ display: 'flex', marginLeft: 'auto', minWidth: 150 }}
                />
            </form>
        </div>
    );
};

export default NewUserInvitation;
